/* UpdateDeviceStatusesAction.js */

"use strict";

const UpdateDeviceStatusesVeilComponent = require("./UpdateDeviceStatusesVeilComponent");

const STORAGE_KEYS = {
    STATUS: "lastDeviceStatusUpdate-status",
    COMMENT: "lastDeviceStatusUpdate-comment",
};

// Plan statuses where device status editing is disabled
const DEVICE_STATUS_EDIT_DISABLED_STATUSES = [
    "DRAFT",
    "VALIDATED",
    "VALIDATING",
    "VALIDATION_ERROR",
    "INVALID",
    "BUNDLING",
    "BUNDLE_ERROR",
    "UPLOADING_RESULTS",
    "UPGRADE_COMPLETE",
];

class UpdateDeviceStatusesAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._userName = this._gameEditor._userSession.user.userName;

        this.run = this.run.bind(this);
    }

    /*******************************************/
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _buildDeviceContext(selectedLink) {
        if (!selectedLink.selectedRows) {
            return {
                deviceIds: [selectedLink.id || selectedLink.getId()],
                deviceName: selectedLink.name || selectedLink.getName() || "Unknown Device",
            };
        }
        return {
            deviceIds: selectedLink.selectedRows,
        };
    }

    _buildStatusInfo(currentPlanInstance, data) {
        const deviceStatusFromPlan = currentPlanInstance.getDeviceUpgradeStatuses();

        if (!deviceStatusFromPlan) {
            throw new Error("No device status found in plan");
        }

        return Object.keys(deviceStatusFromPlan).reduce((acc, deviceId) => {
            acc[deviceId] = data.deviceContext.deviceIds.includes(deviceId)
                ? {
                      status: data.status,
                      comment: data.comment,
                      userName: this._userName,
                      timestamp: new Date().getTime(),
                  }
                : deviceStatusFromPlan[deviceId];
            return acc;
        }, {});
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    run() {
        const selectedLink = this._activeView._selectedLinkForStatusUpdate;
        let errorMsg = "";

        const plan = this._gameEditor.getPlan();
        const planStatus = plan.getStatus();

        if (DEVICE_STATUS_EDIT_DISABLED_STATUSES.includes(planStatus)) {
            errorMsg =
                "IoT Device status can only be updated when the IoT OS Plan's Progress Status is BUNDLED, UPGRADE INCOMPLETE or UPGRADE ERROR.";
        }
        if (!selectedLink || selectedLink.selectedRows?.length === 0) {
            errorMsg = "Please select at least one device to update its status.";
        }

        if (errorMsg) {
            return this._modalUserFeedback.hideDisplayed().then(() =>
                this._modalUserFeedback.displayNotification(errorMsg, {
                    title: "Update IoT Device Statuses Not Available",
                })
            );
        }

        const deviceContext = this._buildDeviceContext(selectedLink);
        const deviceCount = deviceContext.deviceIds.length;
        const veilComponent = new UpdateDeviceStatusesVeilComponent(deviceContext, deviceCount);

        return this._modalUserFeedback
            .displayCustom(veilComponent, {
                title: "Update Device Statuses",
            })
            .then((data) => {
                window.sessionStorage.setItem(STORAGE_KEYS.STATUS, data.status);
                window.sessionStorage.setItem(STORAGE_KEYS.COMMENT, data.comment);

                this._modalUserFeedback.hideDisplayed();

                // Load fresh plan instance for update
                const planId = plan.id;

                return this._gameEditor._planEntity
                    .loadOdmOsUpgradePlan(this._gameEditor.activeOrgId, planId)
                    .then((currentPlanInstance) => {
                        const statusInfo = this._buildStatusInfo(currentPlanInstance, data);
                        currentPlanInstance.setProperty("deviceUpgradeStatuses", statusInfo);

                        if (currentPlanInstance.isDirty()) {
                            return currentPlanInstance.update();
                        }

                        return Promise.resolve();
                    });
            })
            .catch((error) => {
                if (error?.userRejected) {
                    throw error;
                }
                this._logger.warn({error}, "UpdateDeviceStatusesAction.run error");
                return this._modalUserFeedback
                    .hideDisplayed()
                    .then(() => this._modalUserFeedback.displayError(error));
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = UpdateDeviceStatusesAction;

UpdateDeviceStatusesAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
];

UpdateDeviceStatusesAction.$displayDetails = {
    type: "Action",
    name: "UpdateDeviceStatusesAction",
    icon: "osmanager.edit_os_plan_associations",
    shown: true,
    title: "Update IoT Device Statuses",
    order: 2,
};
